/**
 * ABTestingPanel Component
 *
 * Adds an A/B testing layer to the WhatsApp campaign flow. The marketer
 * toggles experimentation on, picks a second template variant, sets the
 * split percentage, and chooses the winning criterion. After sending,
 * the backend will pick the winner and ramp the remaining audience.
 */

import { useState } from "react";
import {
  TestTube2,
  ChevronDown,
  Trophy,
  Eye,
  Reply,
  MousePointerClick,
  X,
  Sparkles,
  Loader2,
  Info,
} from "lucide-react";
import type { WhatsAppTemplate } from "../types";

export type WinnerCriterion = "open_rate" | "reply_rate" | "ctr";

interface ABTestingPanelProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;

  templates: WhatsAppTemplate[];
  templatesLoading: boolean;

  variantBTemplate: WhatsAppTemplate | null;
  onSelectVariantB: (templateId: string) => void;

  splitPct: number; // 0-100, % assigned to variant A
  onSplitChange: (value: number) => void;

  criterion: WinnerCriterion;
  onCriterionChange: (c: WinnerCriterion) => void;

  /** Variant A template (already chosen in the main flow) — for context */
  variantATemplate: WhatsAppTemplate | null;
}

const CRITERIA: { value: WinnerCriterion; label: string; icon: typeof Eye }[] = [
  { value: "open_rate", label: "% Lectura", icon: Eye },
  { value: "reply_rate", label: "% Respuesta", icon: Reply },
  { value: "ctr", label: "% Click en CTA", icon: MousePointerClick },
];

export function ABTestingPanel({
  enabled,
  onToggle,
  templates,
  templatesLoading,
  variantBTemplate,
  onSelectVariantB,
  splitPct,
  onSplitChange,
  criterion,
  onCriterionChange,
  variantATemplate,
}: ABTestingPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Filter out the variant A so it can't be picked as B
  const availableForB = templates.filter(
    (t) =>
      t.approvalStatus === "approved" && t.id !== variantATemplate?.id,
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => onToggle(!enabled)}
        className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-50/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
              enabled
                ? "bg-violet-100 text-violet-600"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            <TestTube2 size={17} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              Prueba A/B
              <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded-full uppercase tracking-wider">
                Premium
              </span>
            </h3>
            <p className="text-xs text-slate-500">
              {enabled
                ? "Activo · la IA selecciona el ganador automáticamente"
                : "Probá dos plantillas a la vez y la IA elige la mejor"}
            </p>
          </div>
        </div>

        {/* Toggle switch */}
        <div
          className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
            enabled ? "bg-violet-600" : "bg-slate-300"
          }`}
        >
          <div
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${
              enabled ? "translate-x-6" : "translate-x-0"
            }`}
          />
        </div>
      </button>

      {/* Expanded panel */}
      {enabled && (
        <div className="border-t border-slate-100 p-6 space-y-5 animate-fade-in">
          {/* Variant B selector */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Variante B
            </label>
            <div className="relative">
              <button
                onClick={() => !templatesLoading && setIsOpen(!isOpen)}
                disabled={templatesLoading}
                className={`
                  w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all
                  ${
                    isOpen
                      ? "border-violet-300 ring-2 ring-violet-100"
                      : "border-slate-200 hover:border-slate-300"
                  }
                `}
              >
                {templatesLoading ? (
                  <span className="text-slate-400 flex items-center gap-2 text-sm">
                    <Loader2 size={14} className="animate-spin" />
                    Cargando...
                  </span>
                ) : variantBTemplate ? (
                  <span className="text-sm font-medium text-slate-900">
                    {variantBTemplate.name}
                  </span>
                ) : (
                  <span className="text-sm text-slate-400">
                    Elegí la segunda plantilla
                  </span>
                )}
                <ChevronDown
                  size={16}
                  className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-lg z-30 overflow-hidden animate-fade-in max-h-64 overflow-y-auto">
                  {availableForB.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-400">
                      No hay otros templates aprobados disponibles
                    </div>
                  ) : (
                    availableForB.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => {
                          onSelectVariantB(t.id);
                          setIsOpen(false);
                        }}
                        className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 transition-colors flex items-center justify-between ${
                          t.id === variantBTemplate?.id
                            ? "bg-violet-50 text-violet-700"
                            : "text-slate-700"
                        }`}
                      >
                        <span className="font-medium">{t.name}</span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {t.language}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Split */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">
                División de audiencia
              </label>
              <span className="text-xs text-slate-500">
                Se enviará a una muestra antes de elegir ganador
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide mb-0.5">
                  Variante A
                </p>
                <p className="text-2xl font-bold text-slate-900">{splitPct}%</p>
                <p className="text-[10px] text-slate-500 truncate">
                  {variantATemplate?.name || "Sin plantilla"}
                </p>
              </div>
              <div className="px-4 py-3 bg-violet-50 border border-violet-100 rounded-xl">
                <p className="text-[10px] font-semibold text-violet-700 uppercase tracking-wide mb-0.5">
                  Variante B
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {100 - splitPct}%
                </p>
                <p className="text-[10px] text-slate-500 truncate">
                  {variantBTemplate?.name || "Sin plantilla"}
                </p>
              </div>
            </div>

            <input
              type="range"
              min={10}
              max={90}
              step={5}
              value={splitPct}
              onChange={(e) => onSplitChange(parseInt(e.target.value, 10))}
              className="w-full accent-violet-600"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
              <span>10%</span>
              <span>50/50</span>
              <span>90%</span>
            </div>
          </div>

          {/* Winner criterion */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Criterio del ganador
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CRITERIA.map((c) => (
                <button
                  key={c.value}
                  onClick={() => onCriterionChange(c.value)}
                  className={`
                    p-3 rounded-xl border-2 transition-all text-center
                    ${
                      criterion === c.value
                        ? "border-violet-300 bg-violet-50"
                        : "border-slate-100 hover:border-slate-200"
                    }
                  `}
                >
                  <c.icon
                    size={15}
                    className={`mx-auto mb-1 ${
                      criterion === c.value
                        ? "text-violet-600"
                        : "text-slate-400"
                    }`}
                  />
                  <p
                    className={`text-xs font-medium ${
                      criterion === c.value
                        ? "text-violet-700"
                        : "text-slate-600"
                    }`}
                  >
                    {c.label}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Explanation */}
          <div className="flex items-start gap-2 px-4 py-3 bg-violet-50/50 rounded-xl border border-violet-100">
            <Trophy
              size={14}
              className="text-violet-600 mt-0.5 flex-shrink-0"
            />
            <p className="text-xs text-violet-700 leading-relaxed">
              <strong>Cómo funciona:</strong> enviamos cada variante a la
              muestra indicada. Tras 4hs medimos el criterio elegido y la
              IA envía el ganador al resto de la audiencia automáticamente.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ABTestingPanel;
